import { Link, useRouterState } from '@tanstack/react-router'
import {
  LayoutDashboard,
  Building2,
  CreditCard,
  Users,
  Bell,
  FileText,
  Settings,
  LogOut,
  Package,
  X,
  ShieldCheck,
  Wrench,
  MessageCircle,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { Logo } from '@/components/Logo'

interface NavItem {
  label: string
  href: string
  icon: React.ReactNode
}

const navItems: NavItem[] = [
  {
    label: 'Dashboard',
    href: '/super-admin',
    icon: <LayoutDashboard className="w-5 h-5" />,
  },
  {
    label: 'Organizações',
    href: '/super-admin/organizations',
    icon: <Building2 className="w-5 h-5" />,
  },
  {
    label: 'Planos',
    href: '/super-admin/plans',
    icon: <Package className="w-5 h-5" />,
  },
  {
    label: 'Usuários',
    href: '/super-admin/users',
    icon: <Users className="w-5 h-5" />,
  },
  {
    label: 'Super Admins',
    href: '/super-admin/super-admins',
    icon: <ShieldCheck className="w-5 h-5" />,
  },
  {
    label: 'Cobrança',
    href: '/super-admin/billing',
    icon: <CreditCard className="w-5 h-5" />,
  },
  {
    label: 'Alertas',
    href: '/super-admin/alerts',
    icon: <Bell className="w-5 h-5" />,
  },
  {
    label: 'Logs de Auditoria',
    href: '/super-admin/audit-logs',
    icon: <FileText className="w-5 h-5" />,
  },
  {
    label: 'Manutenção',
    href: '/super-admin/maintenance',
    icon: <Wrench className="w-5 h-5" />,
  },
  {
    label: 'Meta WhatsApp',
    href: '/super-admin/meta-whatsapp',
    icon: <MessageCircle className="w-5 h-5" />,
  },
  {
    label: 'Configurações',
    href: '/super-admin/settings',
    icon: <Settings className="w-5 h-5" />,
  },
]

interface SuperAdminSidebarProps {
  onLogout?: () => void
  onClose?: () => void
}

export function SuperAdminSidebar({ onLogout, onClose }: SuperAdminSidebarProps) {
  const routerState = useRouterState()
  const currentPath = routerState.location.pathname

  const isActive = (href: string) => {
    if (href === '/super-admin') {
      return currentPath === '/super-admin'
    }
    return currentPath.startsWith(href)
  }

  return (
    <aside className="w-60 bg-gray-900 text-white flex flex-col h-full">
      {/* Logo */}
      <div className="p-5 border-b border-gray-800 flex items-center justify-between">
        <Link to="/super-admin" className="flex items-center gap-3" onClick={onClose}>
          <Logo size={38} />
          <div>
            <h1 className="font-semibold text-base">SecBrain</h1>
            <p className="text-xs text-gray-400">Super Admin</p>
          </div>
        </Link>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden p-1 rounded-lg text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={onClose}
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
              isActive(item.href)
                ? 'bg-teal-600 text-white'
                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
            )}
          >
            {item.icon}
            <span className="font-medium">{item.label}</span>
          </Link>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-3 border-t border-gray-800">
        <button
          onClick={onLogout}
          className="flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">Sair</span>
        </button>
      </div>
    </aside>
  )
}
